import React from "react";
import { motion } from "motion/react";

export const HeroBackground: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none z-0">
      {/* Primary dark gradient base */}
      <div className="absolute inset-0 bg-[#050816] bg-gradient-to-b from-[#08111f] via-[#050816] to-[#050816]" />

      {/* Floating blurred ambient glow 1 (Gold/Amber) */}
      <motion.div
        animate={{
          x: [0, 30, -20, 0],
          y: [0, -25, 15, 0],
          scale: [1, 1.15, 0.95, 1],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -top-20 left-1/4 w-[450px] h-[450px] bg-amber-500/10 rounded-full blur-[120px]"
      />

      {/* Floating blurred ambient glow 2 (Deep Cyan/Blue SaaS accent) */}
      <motion.div
        animate={{
          x: [0, -40, 25, 0],
          y: [0, 30, -20, 0],
          scale: [1, 0.9, 1.1, 1],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/3 -right-20 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[140px]"
      />

      {/* Floating blurred ambient glow 3 (Center Gold subtle highlight) */}
      <motion.div
        animate={{
          opacity: [0.15, 0.25, 0.15],
          scale: [1, 1.08, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-600/5 rounded-full blur-[160px]"
      />

      {/* Modern SaaS grid overlay */}
      <div 
        className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_30%,#000_70%,transparent_100%)]" 
      />

      {/* Tiny glowing particle dots */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-[15%] left-[20%] w-1.5 h-1.5 bg-amber-400 rounded-full blur-[1px] animate-pulse" />
        <div className="absolute top-[40%] left-[80%] w-2 h-2 bg-amber-300 rounded-full blur-[1px] animate-pulse [animation-delay:1s]" />
        <div className="absolute top-[75%] left-[35%] w-1.5 h-1.5 bg-blue-400 rounded-full blur-[1px] animate-pulse [animation-delay:2s]" />
        <div className="absolute top-[25%] left-[65%] w-1 h-1 bg-amber-200 rounded-full blur-[0.5px] animate-pulse [animation-delay:1.5s]" />
        <div className="absolute top-[85%] left-[85%] w-2 h-2 bg-amber-400/80 rounded-full blur-[1px] animate-pulse [animation-delay:0.5s]" />
      </div>
    </div>
  );
};

// Animated Number Counter component
interface AnimatedNumberProps {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
}) => {
  const [displayValue, setDisplayValue] = React.useState(value);

  React.useEffect(() => {
    let startTimestamp: number | null = null;
    const duration = 600; // ms
    const startValue = displayValue;
    const targetValue = value;

    if (startValue === targetValue) return;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (targetValue - startValue) * easeProgress;
      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    const animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, [value]);

  const formatted = React.useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(displayValue);
  }, [displayValue, decimals]);

  return (
    <span className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};
